import { ChangeEvent, useEffect, useRef, useState } from "react";
import useFirestore from "shared/hooks/useFirestore";
import useGetBranchId from "shared/hooks/useGetBranchId";
import useStorage from "shared/hooks/useStorage";
import { parseDate } from "shared/utils/dateUtils";

interface inputs{
    title:string,
    content:string,
    student:boolean,
    teacher:boolean,
    expires_at: string,
}

const useRegisterNotice = (clickedNotice:{id:string, writer:string}, isBranch:boolean, isView:boolean) => {
    const uid = sessionStorage.getItem("uid") || "";
    const { addFireStore, readFirestoreOneDoc, updateFirestore, deleteFirestore } = useFirestore();
    const {getBranchId} = useGetBranchId();
    const {uploadStorage} = useStorage();
    const [progress, setProgress] = useState(0);
    const [showProgres, setShowProgress] = useState(false);
    const [isSucceed, setIsSucceed] = useState<boolean>(false);
    const inputFileRef = useRef<HTMLInputElement>(null);
    const inputImageRef = useRef<HTMLInputElement>(null);
    const [file, setFile] = useState<File | null>(null);
    const [images, setImages] = useState<(File | null)[]>([]);
    const [isOpenDeleteSucceedModal, setIsOpenDeleteSucceedModal] = useState(false)
    const [previewImages, setPreviewImages] = useState<(string | ArrayBuffer | null)[]>([]);
    const [inputs, setInputs] = useState<inputs>({
        title:"",
        content:"",
        student:false,
        teacher:false,
        expires_at: parseDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
    })

    useEffect(() => {
        if(clickedNotice.id){
            getNoticeData();
        }
        // eslint-disable-next-line
    }, [clickedNotice])

    const getNoticeData = async() => {
        if(clickedNotice.writer === "master"){
            await readFirestoreOneDoc("Notice/Master/All", clickedNotice.id).then(res => {
                if(res.exists()){
                    const data = res.data();
                    setInputs({
                        title:data.title,
                        content:data.content,
                        student:data.student,
                        teacher:data.teacher,
                        expires_at:  data.expires_at
                    });
                    setPreviewImages(data.images || []);
                }
            })
            return;
        }else{
            const branch_id = await getBranchId();
            await readFirestoreOneDoc(`Notice/Branch/${branch_id}`, clickedNotice.id).then(res => {
                if(res.exists()){
                    const data = res.data();
                    setInputs({
                        title:data.title,
                        content:data.content,
                        student:data.student,
                        teacher:data.teacher,
                        expires_at: data.expires_at
                    });
                    setPreviewImages(data.images || []);
                }
            })
        }
    }

    const onFileInput = (e:ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files && e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
        }
    }

    const onFileInputClick = () => {
        if(file){
            setFile(null);
            return;
        }
        inputFileRef.current?.click();
    }

    const getFileSize = () => {
        if(file){
            return file.size / (1024 * 1024);
        }
        return 0;
    }
    
    const getFileName = () => {
        if(file){
            return `( ${file.name.slice(0, 10)}... ) 클릭하시면 파일이 삭제 됩니다`;
        }
        return "여기를 클릭하시면 파일을 첨부할 수 있습니다";
    }

    const onImageInputClick = () => {
        inputImageRef.current?.click();
    }

    const onImageInput = (e:ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = e.target.files;
        if (selectedFiles && selectedFiles.length > 0) {
            const selectedFile = selectedFiles[0];
            const image = selectedFile;
            encodeImageToFileBlob(image);
            setImages(prev => [...prev, image]);
        }
    }

    const encodeImageToFileBlob = (image:any) => {
        const reader = new FileReader();
        reader.readAsDataURL(image);
        reader.onload = () => {
            if (reader.result) {
                setPreviewImages(prevImages => [...prevImages, reader.result]);
            }
        };
    }

    const showImageListView = () => {
        if(previewImages.length){
            return previewImages.map((item, index) => {
                return <img key={index + 1} width={"100%"} src={typeof item === 'string' ? item : undefined} alt="preview"/>
            });
        }
    }
    
    const onChange = (e:ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) => {
        const {name, value, type} = e.target;

        if(type === "checkbox"){
            const checked = name === "student" ? !inputs.student : !inputs.teacher;
            setInputs(prev => ({
                ...prev,
                [name]: checked
            }));
        }else if(name === "student"){
            let checked = inputs.student;
            if(checked){
                checked = false;
            }else{
                checked = true;
            }
            setInputs({
                ...inputs,
                "student":checked
            })
        }else if(name === "teacher"){
            let checked = inputs.teacher;
            if(checked){
                checked = false;
            }else{
                checked = true;
            }
            setInputs({
                ...inputs,
                "teacher":checked
            })
        }else{
            setInputs({
                ...inputs,
                [name]:value
            })
        }
    }
    const onSaveNoticeClick = () => {
        if(isView && clickedNotice.id){
            updateNotice();
        }else{
            registerNotice();
        }
    }

    const updateNotice = async() => {
        const isMaster = await readFirestoreOneDoc(`User/Auth/Master`, uid).then(res => {
            if(res.exists()){
                return true;
            }
            return false;
        })
        if(clickedNotice.writer === "master" && isMaster){
            await updateFirestore("Notice/Master/All", clickedNotice.id, inputs).then(res => {
                if(res){
                    setIsSucceed(true);
                }
            })
            return;
        }else if(clickedNotice.writer === "branch"){
            const branch_id = await getBranchId();
            await updateFirestore(`Notice/Branch/${branch_id}`, clickedNotice.id, inputs).then(res => {
                if(res){
                    setIsSucceed(true);
                }
            })
        }
        const branch_id = await getBranchId();
        return;
    }

    const registerNotice = async() => {
        const date = new Date();
        let image_urls =[];
        setShowProgress(true);
        for(const image of images){
            if(image){
                setProgress(0);
                const url = await uploadStorage("Notice", image, setProgress).then(res => {
                    if(res){
                        return res;
                    }
                })
                image_urls.push(url);
            }
        }
        setShowProgress(false);
        if(isBranch){
            let data = {
                ...inputs,
                student:true,
                writer:"branch",
                register_date:date,
                images:image_urls,
            }
            const branch_id = await getBranchId();
            addFireStore(`Notice/Branch/${branch_id}`, data).then(res => {
                if(res){
                    setIsSucceed(true);
                    return;
                }
            })
            return;
        }
        let data = {
            ...inputs,
            writer:"master",
            register_date:date,
            images:image_urls,
        }
        addFireStore("Notice/Master/All", data).then(res => {
            if(res){
                setIsSucceed(true);
            }
        });
    }

    const onDeleteClickHandler = async() => {
        if(isBranch && clickedNotice.writer === "branch"){
            const branch_id = await getBranchId();
            deleteFirestore(`Notice/Branch/${branch_id}`, clickedNotice.id).then(res => {
                if(res){
                    setIsOpenDeleteSucceedModal(true);
                }
            })
        }else if(!isBranch && clickedNotice.writer === "master"){
            deleteFirestore(`Notice/Master/All`, clickedNotice.id).then(res => {
                if(res){
                    setIsOpenDeleteSucceedModal(true);
                }
            })
        }
    }
    
    const onSucceedModalConfirmClickHandler = () => {
        setImages([]);
        setPreviewImages([]);
        setInputs({
            title:"",
            content:"",
            student:false,
            teacher:false,
            expires_at: parseDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
        })
    }

    return{file, inputFileRef, onFileInput, onFileInputClick, getFileName, getFileSize, 
        inputs, onChange, 
        inputImageRef, onImageInput, onImageInputClick, showImageListView, 
        onSaveNoticeClick, isSucceed, setIsSucceed,
        onDeleteClickHandler,
        isOpenDeleteSucceedModal, setIsOpenDeleteSucceedModal, progress, showProgres, onSucceedModalConfirmClickHandler
    };
}

export default useRegisterNotice;